import React, { Fragment, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import tw from 'twin.macro'
import { useFormikContext } from 'formik'
import { useSelector } from 'react-redux'
import { Button } from 'src/components/shared/Button'
import { ConfirmActionModal } from 'src/components/shared/ConfirmActionModal'
import { DataRow } from 'src/components/shared/DataRow'
import { LEGAL_STRUCTURE_OPTIONS, OnboardingStep } from 'src/constants/payments-ops/bankOnboarding'
import type { BankOnboardingFormValues } from 'src/interfaces/bankOnboarding'
import { getBankOnboardingSubmitting } from 'src/selectors/bankOnboarding'
import { getCustomers } from 'src/selectors/customer'
import { addS } from 'src/utils/baseUtils'
import { resolveCustomerDisplayName } from 'src/utils/customerList'
import { maskIdentifier } from 'src/utils/paymentsOps/bankOnboardingForm'

interface Props {
  onBack(): void
  onSubmit(values: BankOnboardingFormValues): Promise<boolean>
  /** Resolves the first step whose validator fails for these values, or `null` when all pass. */
  validateAllSteps(values: BankOnboardingFormValues): Promise<OnboardingStep | null>
  onInvalidStep(step: OnboardingStep): void
}

const ReviewStepComponent = ({ onBack, onSubmit, validateAllSteps, onInvalidStep }: Props) => {
  const { values } = useFormikContext<BankOnboardingFormValues>()
  const customers = useSelector(getCustomers)
  const isRequestInFlight = useSelector(getBankOnboardingSubmitting)
  const [isConfirming, setIsConfirming] = useState<boolean>(false)
  // True from the confirm click until the outcome is known (covers the re-validation too).
  const [isChecking, setIsChecking] = useState<boolean>(false)
  const isMountedRef = useRef<boolean>(true)

  useEffect(() => {
    isMountedRef.current = true

    return () => {
      isMountedRef.current = false
    }
  }, [])

  const isBusy = isRequestInFlight || isChecking

  const partnerName = useMemo(() => {
    const partner = customers.find((customer) => {
      return customer.id === values.partnerId
    })

    return partner ? resolveCustomerDisplayName(partner) : values.partnerId
  }, [customers, values.partnerId])

  const legalStructureLabel = useMemo(() => {
    return (
      LEGAL_STRUCTURE_OPTIONS.find((option) => {
        return option.value === values.legalStructure
      })?.label ?? '—'
    )
  }, [values.legalStructure])

  const ownerCount = values.hasAnyBeneficialOwner ? values.beneficialOwners.length : 0

  const handleOpenConfirm = useCallback(() => {
    setIsConfirming(true)
  }, [])

  // Not dismissable (button, backdrop or Escape) while the check or the request is running.
  const handleCloseConfirm = useCallback(() => {
    if (isBusy) {
      return
    }

    setIsConfirming(false)
  }, [isBusy])

  const handleConfirm = useCallback(async () => {
    setIsChecking(true)

    const invalidStep = await validateAllSteps(values)

    if (!isMountedRef.current) {
      return
    }

    if (invalidStep) {
      setIsChecking(false)
      setIsConfirming(false)
      onInvalidStep(invalidStep)

      return
    }

    const isSubmitted = await onSubmit(values)

    // On success the page navigates away and this step unmounts — touch no state then.
    if (!isMountedRef.current) {
      return
    }

    // On success stay busy: the page is navigating away and the confirm must not fire twice.
    if (!isSubmitted) {
      setIsChecking(false)
      setIsConfirming(false)
    }
  }, [onInvalidStep, onSubmit, validateAllSteps, values])

  return (
    <Fragment>
      <div css={tw`flex flex-col gap-4`}>
        <div css={tw`flex flex-col gap-2 rounded-lg bg-shades-50 p-4`}>
          <DataRow title="Partner" text={partnerName} />
          <DataRow title="Virtual account nickname" text={values.virtualAccountNickName} />
          <DataRow
            title="Program manager"
            text={`${values.programManagerName} · ${values.contactEmailAddress}`}
          />
          <DataRow
            title="GigSafe EIN"
            text={maskIdentifier(values.programManagerIdentificationNumber)}
          />
        </div>
        <div css={tw`flex flex-col gap-2 rounded-lg bg-shades-50 p-4`}>
          <DataRow title="Legal name" text={values.legalName} />
          <DataRow
            title={values.entityIdentificationType || 'EIN/TIN'}
            text={maskIdentifier(values.entityIdentificationNumber)}
          />
          <DataRow title="Legal structure" text={legalStructureLabel} />
          <DataRow title="DBA" text={values.hasAnyDba ? values.dbaName : 'None'} />
          <DataRow
            title="Business address"
            text={`${values.businessAddressLine1}, ${values.businessCity}, ${values.businessState} ${values.businessZipCode}`}
            direction="column"
          />
          <DataRow title="Beneficial owners" text={String(ownerCount)} />
        </div>
        <div css={tw`flex flex-col gap-2 rounded-lg bg-shades-50 p-4`}>
          <DataRow
            title="Control person"
            text={`${values.controlPersonFirstName} ${values.controlPersonLastName} (${values.controlPersonTitle})`}
          />
          <DataRow
            title="Control person ID"
            text={`${values.controlPersonIdentificationType} ${maskIdentifier(
              values.controlPersonIdentificationNumber,
            )}`}
          />
          <DataRow
            title="Attestor"
            text={`${values.attestorFirstName} ${values.attestorLastName} · ${values.attestorJobTitle}`}
          />
          <DataRow
            title="Attested on"
            text={values.dateOfAttestation?.format('MMM D, YYYY') ?? '—'}
          />
        </div>
        <div css={tw`flex flex-row justify-between gap-2`}>
          <Button size="small" color="grey" disabled={isBusy} onClick={onBack}>
            ← Back
          </Button>
          <Button
            size="small"
            color="dark"
            disabled={isBusy}
            onClick={handleOpenConfirm}
            data-testid="onboarding-submit"
          >
            Review & submit…
          </Button>
        </div>
      </div>
      <ConfirmActionModal
        isOpen={isConfirming}
        title="Submit onboarding?"
        description={`Onboard ${values.legalName} (${partnerName}) — ${
          values.entityIdentificationType
        } ${maskIdentifier(values.entityIdentificationNumber)}, ${ownerCount} beneficial ${addS(
          ownerCount,
          'owner',
        )}? This creates a live banking relationship with US Bank and cannot be undone from this screen.`}
        confirmLabel="Submit onboarding"
        isSubmitting={isBusy}
        onConfirm={handleConfirm}
        onClose={handleCloseConfirm}
        confirmDataTestId="onboarding-confirm"
      />
    </Fragment>
  )
}

export const ReviewStep = memo(ReviewStepComponent)
